import { INavLink } from "core-integration/src/hooks/types";
import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getServerError } from "core-integration/src/store/global/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import styled, { keyframes } from "styled-components";
import { getIsWebView } from "../../utils/webView";
import ListItem from "./ListItem";

const StyledNav = styled.nav`
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.colors.primaryTwo};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.3s ease;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    position: static;
    box-shadow: none;
  }
`;

// Keyframe animations for the underline effect
const expandRight = keyframes`
  0% { left: 50%; width: 0%; }
  100% { left: 50%; width: 50%; }
`;

const expandLeft = keyframes`
  0% { right: 50%; width: 0%; }
  100% { right: 50%; width: 50%; }
`;

const contractRight = keyframes`
  0% { left: 50%; width: 50%; }
  100% { left: 50%; width: 0%; }
`;

const contractLeft = keyframes`
  0% { right: 50%; width: 50%; }
  100% { right: 50%; width: 0%; }
`;

const NavContainer = styled.div`
  max-width: 1220px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 100%;
  }
`;

const StyledUL = styled.ul`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[4]};
  margin: 0;
  padding-inline-start: ${({ theme }) => theme.space[4]};
  padding-inline-end: ${({ theme }) => theme.space[4]};
  background-color: ${({ theme }) => theme.colors.primaryTwo};
  white-space: nowrap;
  list-style: none;
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  /* Global styles for navigation tabs with animations */
  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab {
    position: relative;
    display: inline-block;
    padding: ${({ theme }) => theme.space[1]} 0;
    color: ${({ theme }) => theme.colors.white};
    font-family: ${({ theme }) => theme.fonts.base};
    font-size: ${({ theme }) => theme.fontSizes[4]};
    line-height: 2.4rem;
    letter-spacing: -0.0175rem;
    text-decoration: none;

    /* Common styles for both pseudo elements */
    &::before,
    &::after {
      position: absolute;
      bottom: 0;
      height: 4px;
      content: "";
      background-color: ${({ theme }) => theme.colors.secondary};
    }

    &::before {
      border-top-left-radius: ${({ theme }) => theme.radii[2]};
    }

    &::after {
      border-top-right-radius: ${({ theme }) => theme.radii[2]};
    }

    @media (prefers-reduced-motion: reduce) {
      &::before,
      &::after {
        animation: none !important;
      }
    }
  }

  /* Active state styles */
  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab.active {
    font-family: ${({ theme }) => theme.fonts.base};
    font-weight: bold;
    background-color: transparent;

    &::before {
      left: 0;
      width: 50%;
    }

    &::after {
      right: 0;
      width: 50%;
    }
  }

  /* Animation rules ordered by specificity */
  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab:not(.active)::before {
    animation: ${contractLeft} 0.1s ease-in-out forwards;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab:not(.active)::after {
    animation: ${contractRight} 0.1s ease-in-out forwards;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab:not(.active):hover::before {
    animation: ${expandLeft} 0.1s ease-in-out forwards;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .ism-nav__tab:not(.active):hover::after {
    animation: ${expandRight} 0.1s ease-in-out forwards;
  }
`;

const NavigationLinks: React.FC<{ links: INavLink[] }> = ({ links }) => {
  return (
    <StyledNav className="ism-nav">
      <NavContainer>
        <StyledUL>
          {links.map((link) => (
            <ListItem {...link} key={link.text} />
          ))}
        </StyledUL>
      </NavContainer>
    </StyledNav>
  );
};

const mapStateToProps = (state: RootState) => {
  const player = getPlayerData(state);
  const currentEvent = getCurrentEvent(state);
  const nextEvent = getNextEvent(state);
  const serverError = getServerError(state);
  const entry = player && player.entry ? getEntry(state, player.entry) : null;

  return {
    player,
    currentEvent,
    nextEvent,
    serverError,
    entry,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Navigation: React.FC<PropsFromRedux> = ({
  player,
  currentEvent,
  nextEvent,
  serverError,
  entry,
}) => {
  const { t } = useTranslation();
  let links: INavLink[] = [];
  const useRouter = !serverError;

  if (player && player.entry) {
    // Logged in with entry
    links = [
      {
        useRouter,
        href: "/",
        text: currentEvent
          ? t("navigation.status", "Status")
          : t("navigation.home", "Home"),
      },
    ];
    if (nextEvent) {
      links.push({
        useRouter,
        href: "/my-team",
        text: t("navigation.myTeam", "Pick Team"),
      });
    }
    // If the entry has started ...
    if (currentEvent && entry && entry.started_event <= currentEvent.id) {
      links.push({
        useRouter,
        href: `/entry/${player.entry}/event/${currentEvent.id}`,
        text: t("navigation.points", "Points"),
      });
    }
    links.push(
      {
        useRouter,
        href: "/transfers",
        text: t("navigation.transfers", "Transfers"),
      },
      {
        useRouter,
        href: "/leagues",
        text: t("navigation.leagues", "Leagues"),
      },
      {
        useRouter,
        href: "/fixtures",
        text: t("navigation.fixtures", "Fixtures"),
      },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
    );
    if (!getIsWebView()) {
      links.push({
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
      });
    }
    links.push({
      useRouter,
      href: "/help",
      text: t("navigation.help", "Help"),
    });
    if (!getIsWebView()) {
      links.push({
        useRouter: false,
        href: "/ssologout",
        text: t("navigation.signOut", "Sign out"),
      });
    }
  } else if (player) {
    // Logged in, no entry
    links = [
      { useRouter, href: "/", text: t("navigation.home", "Home") },
      {
        useRouter,
        href: "/squad-selection",
        text: t("navigation.squadSelection", "Squad Selection"),
      },
      { useRouter, href: "/help", text: t("navigation.help", "Help") },
    ];
    if (!getIsWebView()) {
      links.push({
        useRouter: false,
        href: "/ssologout",
        text: t("navigation.signOut", "Sign out"),
      });
    }
  } else {
    // Logged Out
    links = [{ useRouter, href: "/", text: t("navigation.home", "Home") }];
    if (!getIsWebView()) {
      links.push({
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
      });
    }
    links.push(
      { useRouter, href: "/help", text: t("navigation.help", "Help") },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
    );
  }
  return <NavigationLinks links={links} />;
};

export { Navigation as NavigationTest };

export default connector(Navigation);
